<template>
  <OperationFormLayoutSkeleton
    v-if="loading"
    isConfigurationForm
  />
  <form @submit.prevent="save">
    <OperationFormLayout
      v-if="!loading && currentOrganisation"
      :id="$route.params.equipmentId"
      :formDisabled="disable"
      :formSaving="saving"
      isConfigurationForm
      operationType="equipment"
      @cancel="back"
    >
      <!-- Details section -->
      <OperationFormSection
        :modifiers="[
          'no-margin',
          'full-max-width',
          'responsive',
          'default',
          'border-bottom',
        ]"
        :sectionDescription="$t('equipment.details_description')"
        :sectionTitle="$t('equipment.details_title')"
      >
        <FormRow
          :errors="nameErrors"
          :label="$t('operations.operation_name_label_equipment')"
          labelFor="operation_name"
          :modifiers="['no-padding']"
        >
          <div class="form-row__input form-row__input--no-peek">
            <div :class="{ input: true, 'input--error': nameErrors }">
              <input
                id="operation_name"
                v-model="equipment.name"
              >
            </div>
          </div>
        </FormRow>
        <FormRow
          :label="$t('equipment.equipment_division_label')"
          :modifiers="['no-padding']"
        >
          <div class="form-row__input form-row__input--no-peek">
            <DivisionSelect
              v-model="equipment.division"
              :divisions="divisions"
            />
          </div>
        </FormRow>
        <FormRow
          v-if="equipment.division && equipment.division.isNew"
          :errors="divisionNameErrors"
          :label="$t('equipment.equipment_new_division_label')"
          labelFor="new_division_name"
          :modifiers="['no-padding']"
        >
          <div class="form-row__input form-row__input--no-peek">
            <div :class="{ input: true, 'input--error': divisionNameErrors }">
              <input
                id="new_division_name"
                v-model="equipment.division.name"
              >
            </div>
          </div>
        </FormRow>
        <FormRow
          :label="$t('equipment.mine')"
          :modifiers="['no-padding']"
        >
          <div class="form-row__input form-row__input--no-peek">
            <Select
              v-model="equipment.mine_id"
              :items="mines"
              :modifiers="['input', 'base', 'short-dropdown']"
              :placeholder="displayMine"
            />
          </div>
        </FormRow>
        <FormRow
          :label="$t('equipment.type')"
          :modifiers="['no-padding']"
        >
          <div class="form-row__input form-row__input--no-peek">
            <Select
              v-model="equipment.equipment_type_id"
              :items="types"
              :modifiers="['input', 'base', 'short-dropdown']"
              :placeholder="displayType"
            />
          </div>
        </FormRow>
        <FormRow
          :label="$t('equipment.process')"
          :modifiers="['no-padding']"
        >
          <div class="form-row__input form-row__input--no-peek">
            <Select
              v-model="equipment.equipment_process_id"
              :items="processes"
              :modifiers="['input', 'base', 'short-dropdown']"
              :placeholder="displayProcess"
            />
          </div>
        </FormRow>
      </OperationFormSection>

      <!-- Configure elements section -->
      <OperationFormSection
        :modifiers="[
          'no-margin',
          'full-max-width',
          'responsive',
          'default',
          'border-bottom',
        ]"
        :sectionDescription="$t('equipment.diagram_help_text')"
        :sectionTitle="$t('equipment.equipment_diagram')"
      >
        <FormRow :modifiers="['no-padding']">
          <template v-if="diagramImage">
            <img
              class="equipment-diagram"
              :src="diagramImage"
            >
            <FormStandAloneErrorMessage
              v-if="!imageValid"
              :errorMessage="imageValidationMessage"
            />
            <div
              :class="{
                'form-layout__row--mobile form-layout__row--inline': $route.params.equipmentId,
                'form-layout__row--margin-top': true,
              }"
            >
              <div
                :class="{ 'form-layout__column form-layout__column--inline-button': $route.params.equipmentId }"
              >
                <label
                  class="diagram-button button"
                  for="equipment_change_diagram"
                >
                  {{ $t('equipment.change_diagram') }}
                  <FileUploader
                    id="equipment_change_diagram"
                    :acceptedFileTypes="acceptedFileTypes"
                    fileType="image"
                    :label="$t('issues.add_image')"
                    :modifiers="['hide']"
                    :multiple="false"
                    @update-blob="updateBlob"
                    @update-reader="updateReader"
                  />
                </label>
              </div>
              <div
                v-if="$route.params.equipmentId"
                class="form-layout__column form-layout__column--inline-button"
              >
                <router-link
                  class="button"
                  :to="{ name: 'equipmentManageRegions' }"
                >
                  <ManageRegionSvg class="button__icon button--margin-right" />
                  {{ $t('equipment.manage_regions') }}
                </router-link>
              </div>
            </div>
          </template>
          <label
            v-else
            class="diagram-button button"
            for="diagram"
          >
            {{ $t('equipment.upload_diagram') }}
            <FileUploader
              id="diagram"
              :acceptedFileTypes="acceptedFileTypes"
              fileType="image"
              :label="$t('issues.add_image')"
              :modifiers="['hide']"
              :multiple="false"
              @update-blob="updateBlob"
              @update-error="updateError"
              @update-reader="updateReader"
            />
          </label>
          <FormStandAloneErrorMessage
            v-if="!imageValid && !diagramImage"
            :errorMessage="imageValidationMessage"
          />
        </FormRow>
      </OperationFormSection>
    </OperationFormLayout>
  </form>
</template>

<script setup>
import { computed, inject, onMounted, reactive, ref, watch } from 'vue';

// Utils
import I18n from 'i18n-js';
import { acceptedFileTypes } from '@/config/accepted_file_types';
import { useRoute, useRouter } from 'vue-router';
import dig from '@/functions/dig';
import useAjax from '@/functions/use_ajax';
import useUploader from '@/vendor/use_uploader';
import isDesktop from '@/functions/responsive';

// Components
import DivisionSelect from '@/components/operations/division_select.vue';
import FormRow from '@/components/form/row.vue';
import OperationFormLayout from '@/components/operations/form/operation_form_layout.vue';
import OperationFormLayoutSkeleton from '@/components/operations/form/operation_form_layout_skeleton.vue';
import OperationFormSection from '@/components/operations/form/operation_form_section.vue';
import Select from '@/components/form/select.vue';
import FormStandAloneErrorMessage from '@/components/form/form_stand_alone_error_message.vue';
import FileUploader from '@/components/generic/file_uploader.vue';

// Svg
import ManageRegionSvg from '@/images/manage-region.svg';

const createNewEquipment = () => ({
  name: '',
  division: null,
  mine_id: '',
  equipment_type_id: '',
  equipment_process_id: '',
  map_tile_overlays: [],
});

const route = useRoute();
const router = useRouter();
const uploader = useUploader();
const ajax = useAjax();
const currentOrganisation = inject('currentOrganisation');
const loading = ref(false);
const initialEquipment = ref(null);
const equipment = reactive(createNewEquipment());
const errors = ref(null);
const saving = ref(false);
const divisions = ref(null);
const diagramImage = ref(null);
const mines = ref([]);
const types = ref([]);
const processes = ref([]);
const disable = ref(true);
let diagramUpload;

const imageValid = ref(true);
const imageValidationMessage = ref(null);
const warningText = ref(null);

// All endpoints that this component uses
const ENDPOINTS = {
  divisions: `/organisations/${route.params.organisationId}/divisions`,
  mines: `/organisations/${route.params.organisationId}/mines`,
  equipmentList: `/organisations/${route.params.organisationId}/equipment/`,
  equipment: `/organisations/${route.params.organisationId}/equipment/${route.params.equipmentId}`,
  types: `/organisations/${route.params.organisationId}/equipment_types`,
  processes: `/organisations/${route.params.organisationId}/equipment_processes`,
  get post_or_patch() {
    return route.params.equipmentId ? this.equipment : this.equipmentList;
  },
};

/**
 * Form validation, check if the form was updated,
 * if form is updated enable the save button
 */
watch(() => equipment, (newValue) => {
    const formFields = [
      'name',
      'division',
      'division_id',
      'mine_id',
      'equipment_type_id',
      'equipment_process_id',
      'time_zone'
    ];

    const changedFields = formFields.filter((field) => {
      // Check if a division exists
      if (!Object.hasOwn(initialEquipment.value, field)) return;
      // Check if a division was set (if it was then a division object will exist)
      if ((typeof newValue[field] === 'object' && newValue[field] !== null) && !initialEquipment.value[field])
        return newValue[field]?.id !== initialEquipment.value['division_id'];
      // Compare division objects
      if (typeof newValue[field] === 'object')
        return (JSON.stringify(newValue[field]) !== JSON.stringify(initialEquipment.value[field]));
      // Compare other string instances
      return newValue[field] !== initialEquipment.value[field];
    });

    // Check for new division
    const hasNewDivision = !Object.hasOwn(initialEquipment.value, 'division') && Object.hasOwn(newValue, 'division') && newValue['division'] !== null;
    disable.value = changedFields.length <= 0 && !hasNewDivision;
  },
  { deep: true }
);

/**
 * Check if diagram was updated
 */
watch(() => diagramImage, (newValue) => {
    disable.value = initialEquipment.value.diagram_thumbnail_url === newValue.value;
  },
  { deep: true }
);

const loadDivisions = async () => {
  let { ok, data } = await ajax(ENDPOINTS.divisions);

  if (ok) {
    divisions.value = data.divisions;
  }
};

const loadMines = async () => {
  let { ok, data } = await ajax(ENDPOINTS.mines);

  if (ok) {
    mines.value = data?.map((item) => ({ value: item.id, label: item.name }));
    mines.value.unshift({ value: '', label: I18n.t('equipment.no_mine'), dim: true });
  }
};

const loadTypes = async () => {
  let { ok, data } = await ajax(ENDPOINTS.types);

  if (ok) {
    types.value = data?.map((item) => ({ value: item.id, label: item.name }));
    types.value.unshift({ value: '', label: I18n.t('equipment.no_type'), dim: true });
  }
};

const loadProcesses = async () => {
  let { ok, data } = await ajax(ENDPOINTS.processes);

  if (ok) {
    processes.value = data?.map((item) => ({ value: item.id, label: item.name }));
    processes.value.unshift({ value: '', label: I18n.t('equipment.no_process'), dim: true });
  }
};

const loadEquipment = async () => {
  if (route.params.equipmentId) {
    loading.value = true;
    let { ok, data } = await ajax(ENDPOINTS.equipment);

    if (ok) {
      initialEquipment.value = data;
      Object.assign(equipment, data);
      diagramImage.value = data?.diagram_thumbnail_url;
      loading.value = false;
    }
  } else {
    initialEquipment.value = createNewEquipment();
  }
};

onMounted(() => {
  loadDivisions();
  loadMines();
  loadTypes();
  loadProcesses();
  loadEquipment();
});

const formatDivisionAttributes = () => {
  if (equipment.division?.isNew) {
    return {
      division_id: 'new',
      new_division: equipment.division.name,
    };
  } else {
    return {
      division_id: equipment.division?.id || null,
    };
  }
};

const formatEquipmentForPost = async () => {
  let equipmentParams = {
    name: equipment.name,
    mine_id: equipment.mine_id,
    equipment_type_id: equipment.equipment_type_id,
    equipment_process_id: equipment.equipment_process_id,
    ...formatDivisionAttributes(),
  };

  if (diagramUpload) {
    let blob = await uploader.upload(diagramUpload);
    equipmentParams.diagram = {
      source: blob.signed_id,
      source_filename: blob.filename,
    };
  }

  return { equipment: equipmentParams };
};

const displayMine = computed(() => {
  return equipment.mine
    ? equipment.mine
    : I18n.t('equipment.no_mine');
});

const displayType = computed(() => {
  return equipment.type
    ? equipment.type
    : I18n.t('equipment.no_type');
});

const displayProcess = computed(() => {
  return equipment.process
    ? equipment.process
    : I18n.t('equipment.no_process');
});

const save = async () => {
  errors.value = null;
  saving.value = true;

  let method = route.params.equipmentId ? 'PATCH' : 'POST';
  let { ok, data } = await ajax(ENDPOINTS.post_or_patch, {
    method,
    body: await formatEquipmentForPost(),
  });

  if (ok) {
    router.replace({
      name: isDesktop() ? 'equipment' : 'equipmentConfiguration',
      params: { equipmentId: data.id }
    });
  } else {
    errors.value = data;
  }

  saving.value = false;
};

const nameErrors = computed(() => dig(errors.value, 'equipment', 'name'));
const divisionNameErrors = computed(() =>
  dig(errors.value, 'division', 'division', 'name')
);

function updateBlob(file) {
  if (!file?.upload) return;
  diagramUpload = file.upload;
}

function updateReader(file) {
  if (!file?.url) return;
  diagramImage.value = file.url;
  warningText.value = I18n.t('general.warning_text');
}

function updateError(file) {
  if (!file?.filename) return;

  if (!acceptedFileTypes.includes(file.filename)) {
    imageValidationMessage.value = I18n.t('general.image_validation_message', {
      filename: file.filename,
    });
    imageValid.value = false;
  } else {
    imageValid.value = true;
  }
}

function back() {
  router.replace({
    name: isDesktop() ? 'equipment' : 'equipmentConfiguration',
    params: { equipmentId: route.params.equipmentId },
  });
}
</script>

<style scoped lang="scss">
@import '@/stylesheets/global';

.equipment-diagram {
  width: 100%;
}
</style>
